import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Bookmark,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Search,
  Send,
  Trash2,
  XCircle
} from "lucide-react";
import { deleteFixture, listFixtures, saveFixture, testEndpoint } from "../../services/inHouseService";
import { CodeBlock, ErrorNote, MethodPill, StepFooter, inputClass } from "./parts";
import ResponseViewer from "./ResponseViewer";
import OrbitLoader from "../ui/OrbitLoader";
import { TRY_REQUEST, useMachine } from "./machines";
import { forgetValues, rememberValues, startingValues } from "./tryMemory";

/**
 * Try it: send a real request to one call. Laid out like the API clients admins already use
 * (Postman, Insomnia, Swagger UI's "Try it out"): one workspace with a searchable list of calls
 * down the side, a request bar with the method, the full URL and Send, inputs in Params /
 * Headers / Body tabs, saved inputs by the call's name, and the response under the request.
 */

const SEARCH_FROM = 6; // a short list doesn't need a search box

// Postman's request tabs; Params holds both path and query inputs.
const TABS = [
  { id: "params", title: "Params", locations: ["path", "query"] },
  { id: "headers", title: "Headers", locations: ["header"] },
  { id: "body", title: "Body", locations: ["body"] }
];

// Order and wording of the input groups inside the tabs.
const LOCATIONS = [
  { id: "path", title: "Path parameters", hint: "Filled into the URL above." },
  { id: "query", title: "Query parameters", hint: "Added after ? in the URL." },
  { id: "header", title: "Headers", hint: "Sent with the request. Sign-in headers are added for you." },
  { id: "body", title: "Body", hint: "Sent as JSON." }
];

const REASON = {
  200: "OK", 201: "Created", 202: "Accepted", 204: "No Content", 301: "Moved", 302: "Found", 304: "Not Modified",
  400: "Bad Request", 401: "Unauthorized", 403: "Forbidden", 404: "Not Found", 405: "Method Not Allowed",
  408: "Timeout", 409: "Conflict", 422: "Unprocessable", 429: "Too Many Requests",
  500: "Server Error", 502: "Bad Gateway", 503: "Unavailable", 504: "Gateway Timeout"
};

// What the response panel says before there is a response to show.
const RESPONSE_PLACEHOLDER = {
  idle: "Send the request to see the response here.",
  sending: "Waiting for your API…",
  rejected: "Not sent. Fix what's described above, then send again."
};

/**
 * The engine copies a failed call's raw body into error_message. When that body is an HTML
 * error page (a wrong path on IIS, a proxy, a login wall), show its title and the likely fix
 * instead of a wall of markup; the page itself is still in the response below.
 */
export function errorSummary(message) {
  if (!message || !/^\s*</.test(message)) return message;
  const title = message.match(/<title[^>]*>([^<]*)<\/title>/i)?.[1]?.trim();
  const page = title ? `an HTML page ("${title}")` : "an HTML page";
  return `Your API answered with ${page} instead of data. Check the call's path and the base URL.`;
}

const coerce = (field, raw) => {
  if (raw === "" || raw === undefined) return undefined;
  if (field.type === "number" || field.type === "integer") {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (field.type === "boolean") return raw === "true" ? true : raw === "false" ? false : raw;
  if (field.type === "object" || field.type === "array") {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
};

const display = (value) =>
  value === null || value === undefined ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);

const formatBytes = (n) => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`);

const bodySize = (body) => {
  if (body === undefined || body === null) return 0;
  const text = typeof body === "string" ? body : JSON.stringify(body);
  return new Blob([text]).size;
};

/**
 * "/claims/{id}" with each placeholder swapped for its value once one is typed. When space is
 * short the base URL gives way first, since the path is what changes between calls.
 */
function UrlPreview({ baseUrl, path, values, fields }) {
  const byName = Object.fromEntries(fields.filter((f) => f.location === "path").map((f) => [f.name, f.key]));
  const parts = path.split(/(\{[^}]+\})/g).filter(Boolean);
  return (
    <span className="flex min-w-0 font-mono text-sm" title={`${baseUrl || ""}${path}`}>
      <span className="min-w-0 truncate text-gray-400">{(baseUrl || "").replace(/\/+$/, "")}</span>
      <span className="max-w-full shrink-0 truncate">
        {parts.map((part, i) => {
          const name = part.startsWith("{") ? part.slice(1, -1) : null;
          if (!name) return <span key={i} className="text-gray-900">{part}</span>;
          const value = values[byName[name]];
          return value ? (
            <span key={i} className="text-blue-700">{encodeURIComponent(value)}</span>
          ) : (
            <span key={i} className="rounded bg-amber-100 px-0.5 text-amber-800">{part}</span>
          );
        })}
      </span>
    </span>
  );
}

function CallList({ endpoints, activeId, tested, onSelect }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = endpoints.filter(
    (e) => !q || e.name.toLowerCase().includes(q) || e.path.toLowerCase().includes(q) || e.http_method.toLowerCase() === q
  );

  return (
    <aside className="flex min-h-0 flex-col border-b border-gray-200 bg-gray-50 lg:border-b-0 lg:border-r">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Calls</h3>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{endpoints.length}</span>
      </div>
      {endpoints.length >= SEARCH_FROM && (
        <div className="relative border-b border-gray-200 bg-white px-3 py-2">
          <Search className="pointer-events-none absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, path or method"
            aria-label="Filter calls"
            className="w-full rounded-md border border-gray-200 py-1.5 pl-7 pr-2 text-sm placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
      )}
      {visible.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-gray-500">Nothing matches “{query.trim()}”.</p>
      ) : (
        <ul className="max-h-64 min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto lg:max-h-none">
          {visible.map((e) => {
            const active = e.id === activeId;
            return (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => onSelect(e.id)}
                  aria-current={active ? "true" : undefined}
                  className={`flex w-full items-start gap-3 border-l-[3px] px-3 py-3 text-left transition ${
                    active ? "border-blue-600 bg-white" : "border-transparent hover:bg-white"
                  }`}
                >
                  <MethodPill method={e.http_method} className="mt-0.5 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm ${active ? "font-semibold text-gray-900" : "font-medium text-gray-800"}`}>
                      {e.name}
                    </span>
                    <span className="block truncate font-mono text-xs text-gray-500">{e.path}</span>
                  </span>
                  {tested.has(e.id) && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-label="Tried" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

/** Saved inputs, the way API clients keep saved examples with a request. */
function SavedInputs({ fixtures, onLoad, onForget, onSave }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : !ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSave(name.trim());
    setBusy(false);
    if (ok) setName("");
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex h-full items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <Bookmark className="h-4 w-4" />
        <span className="hidden sm:inline">Saved</span>
        {fixtures.length > 0 && <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600">{fixtures.length}</span>}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          {fixtures.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500">No saved inputs yet.</p>
          ) : (
            <ul className="max-h-60 divide-y divide-gray-100 overflow-y-auto">
              {fixtures.map((fx) => (
                <li key={fx.id} className="flex items-center gap-1 pr-1">
                  <button
                    type="button"
                    onClick={() => {
                      onLoad(fx);
                      setOpen(false);
                    }}
                    className="min-w-0 flex-1 truncate px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                  >
                    {fx.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => onForget(fx)}
                    aria-label={`Delete saved inputs ${fx.name}`}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={save} className="flex gap-2 border-t border-gray-200 bg-gray-50 p-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Name the current inputs"
              aria-label="Name for the current inputs"
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <button type="submit" disabled={busy} className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50">
              Save
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function FieldInput({ field, value, invalid, onChange }) {
  const cls = `${inputClass(invalid)} py-2`;
  if (field.enum) {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
        <option value="">Choose…</option>
        {field.enum.map((o) => (
          <option key={String(o)} value={String(o)}>{String(o)}</option>
        ))}
      </select>
    );
  }
  if (field.type === "boolean") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
        <option value="">Not set</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (field.type === "object" || field.type === "array") {
    return (
      <textarea
        rows={3}
        value={value || ""}
        placeholder={field.type === "array" ? "[ ]" : "{ }"}
        onChange={(e) => onChange(e.target.value)}
        className={`${cls} font-mono`}
      />
    );
  }
  return (
    <input
      type={field.type === "date" ? "date" : "text"}
      inputMode={field.type === "number" || field.type === "integer" ? "decimal" : undefined}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={`${cls} font-mono`}
    />
  );
}

/** One group of inputs as a key / value table, like an API client's Params or Headers tab. */
function InputGroup({ group, fields, values, fieldErrors, devMode, onChange }) {
  return (
    <section aria-label={group.title}>
      <div className="mb-2 flex items-baseline gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{group.title}</h4>
        <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600">{fields.length}</span>
        <span className="text-xs text-gray-400">{group.hint}</span>
      </div>
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {fields.map((f) => (
          <div key={f.key} className="grid gap-2 px-3 py-2.5 sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)] sm:items-start sm:gap-4">
            <div className="min-w-0 pt-1.5">
              <p className="truncate font-mono text-sm text-gray-900" title={f.key}>{devMode ? f.key : f.name}</p>
              <p className="text-xs text-gray-500">
                {f.type}
                {f.required ? <span className="font-medium text-rose-700"> · required</span> : " · optional"}
              </p>
            </div>
            <div className="min-w-0">
              <FieldInput field={f} value={values[f.key]} invalid={!!fieldErrors[f.key]} onChange={(v) => onChange(f.key, v)} />
              {fieldErrors[f.key] ? (
                <p className="mt-1 text-xs text-rose-600">{fieldErrors[f.key]}</p>
              ) : f.description ? (
                <p className="mt-1 text-xs text-gray-500">{f.description}</p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Colour, icon and words together, so the result reads without colour. */
function StatusLine({ result }) {
  const code = result.http_status_code;
  const ok = code && code < 300;
  const Icon = !code ? AlertTriangle : ok ? CheckCircle2 : XCircle;
  const tone = !code ? "text-amber-700" : ok ? "text-emerald-700" : "text-rose-700";
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className={`flex items-center gap-1.5 font-semibold ${tone}`}>
        <Icon className="h-4 w-4" />
        {code ? `${code} ${REASON[code] || ""}`.trim() : "No response"}
      </span>
      {result.duration_ms != null && <span className="text-gray-500">{result.duration_ms} ms</span>}
      {result.response_body != null && <span className="text-gray-500">{formatBytes(bodySize(result.response_body))}</span>}
    </div>
  );
}

function TryCall({ integration, endpoint, devMode, onTested }) {
  const [start] = useState(() => startingValues(integration.id, endpoint, (f) => display(f.example)));
  const [values, setValues] = useState(start.values);
  const [remembered, setRemembered] = useState(start.remembered);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [request, sendRequest] = useMachine("try request", TRY_REQUEST, "idle");
  const busy = request === "sending";
  const [result, setResult] = useState(null);
  // A fresh viewer per response, so a new test doesn't keep the last one's tab or expansion.
  const [resultKey, setResultKey] = useState(0);
  const responseRef = useRef(null);
  const [fixtures, setFixtures] = useState([]);
  const [tab, setTab] = useState(() => TABS.find((t) => endpoint.fields.some((f) => t.locations.includes(f.location)))?.id);
  const disabled = integration.status === "disabled";

  // The response sits under the inputs; bring it into view when it arrives, as Postman's split pane keeps it.
  useEffect(() => {
    if (resultKey > 0) responseRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [resultKey]);

  useEffect(() => {
    let current = true;
    listFixtures(integration.id, endpoint.id).then((r) => current && r.success && setFixtures(r.data));
    return () => {
      current = false;
    };
  }, [integration.id, endpoint.id]);

  const setValue = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const payload = () =>
    Object.fromEntries(
      endpoint.fields
        .map((f) => [f.key, coerce(f, values[f.key])])
        .filter(([, v]) => v !== undefined)
    );

  const load = (fixture) => {
    if (!sendRequest("reset")) return; // not while a request is in flight
    const saved = fixture.request_payload || {};
    setValues(Object.fromEntries(endpoint.fields.map((f) => [f.key, display(saved[f.key])])));
    setFieldErrors({});
    setResult(null);
  };

  const startFromExamples = () => {
    forgetValues(integration.id, endpoint.id);
    setValues(Object.fromEntries(endpoint.fields.map((f) => [f.key, display(f.example)])));
    setRemembered(false);
  };

  const forget = async (fixture) => {
    const r = await deleteFixture(integration.id, endpoint.id, fixture.id);
    if (r.success) setFixtures((list) => list.filter((x) => x.id !== fixture.id));
    else setError(r.message);
  };

  const save = async (name) => {
    const fallback = `Inputs ${new Date().toLocaleString()}`;
    const r = await saveFixture(integration.id, endpoint.id, { name: (name || fallback).slice(0, 120), request_payload: payload() });
    if (r.success) setFixtures((list) => [r.data, ...list]);
    else setError(r.message);
    return r.success;
  };

  const send = async () => {
    // A repeat press while one is in flight is normal, not a bug: skip it before asking the machine.
    if (disabled || busy || !sendRequest("send")) return;
    setError("");
    setFieldErrors({});
    rememberValues(integration.id, endpoint, values);
    const response = await testEndpoint(integration.id, endpoint.id, payload());
    if (response.success) {
      setResult(response.data);
      setResultKey((k) => k + 1);
      if (response.data.http_status_code && response.data.http_status_code < 300) onTested();
      sendRequest("responded");
    } else {
      setResult(null);
      const errors = response.fields || {};
      setFieldErrors(errors);
      setError(response.message);
      // Open the tab holding the first input with a problem, so the message is in view.
      const location = endpoint.fields.find((f) => errors[f.key])?.location;
      const errorTab = TABS.find((t) => t.locations.includes(location));
      if (errorTab) setTab(errorTab.id);
      sendRequest("rejected");
    }
  };

  // Cmd/Ctrl+Enter sends from anywhere in the request, as in Postman and Insomnia.
  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const groupsIn = (tab) =>
    LOCATIONS.filter((g) => tab.locations.includes(g.id))
      .map((g) => ({ group: g, fields: endpoint.fields.filter((f) => f.location === g.id) }))
      .filter((g) => g.fields.length > 0);
  const tabs = TABS.map((t) => {
    const groups = groupsIn(t);
    const fields = groups.flatMap((g) => g.fields);
    return { ...t, groups, count: fields.length, hasError: fields.some((f) => fieldErrors[f.key]) };
  });
  const shownTab = tabs.find((t) => t.id === tab) || tabs[0];
  const body = result?.response_body;
  // Keys starting with "_" are the engine's wrappers (_raw text, _binary), not response fields.
  const flat = body && typeof body === "object" && !Array.isArray(body)
    ? Object.entries(body).filter(([k, v]) => typeof v !== "object" && !k.startsWith("_"))
    : [];

  return (
    <div className="min-h-0 min-w-0 overflow-y-auto" onKeyDown={onKeyDown}>
      <div className="space-y-3 p-4">
        {/* Saved inputs sit by the call's name, as Postman's Save sits on the request tab, leaving the URL room. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">{endpoint.name}</h3>
            {endpoint.description && <p className="mt-0.5 text-sm text-gray-500">{endpoint.description}</p>}
          </div>
          <SavedInputs fixtures={fixtures} onLoad={load} onForget={forget} onSave={save} />
        </div>

        <div className="flex items-stretch gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-3 rounded-lg border border-gray-300 bg-white px-3 py-2">
            <MethodPill method={endpoint.http_method} className="shrink-0" />
            <UrlPreview baseUrl={integration.api?.base_url} path={endpoint.path} values={values} fields={endpoint.fields} />
          </div>
          <button
            type="button"
            onClick={send}
            disabled={busy || disabled}
            title={disabled ? "Turn the integration back on to send tests" : "Send (Ctrl+Enter)"}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
        <p className="text-xs text-gray-500">
          {disabled
            ? "This integration is disabled. Turn it back on to send tests."
            : "Sends a real request with your saved sign-in, exactly as in production. Ctrl+Enter or ⌘+Enter sends."}
          {remembered && (
            <>
              {" "}Inputs are filled in from your last requests.{" "}
              <button type="button" onClick={startFromExamples} className="font-medium text-blue-700 hover:underline">
                Start from the examples
              </button>
            </>
          )}
        </p>
      </div>

      <div className="border-b border-gray-200 px-4">
        <div role="tablist" aria-label="Request inputs" className="-mb-px flex gap-5">
          {tabs.map((t) => {
            const active = t.id === shownTab.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 border-b-2 py-2 text-sm font-medium ${
                  active ? "border-blue-600 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                {t.title}
                {t.count > 0 && <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600">{t.count}</span>}
                {t.hasError && <AlertCircle className="h-3.5 w-3.5 text-rose-600" aria-label="Has a problem" />}
              </button>
            );
          })}
        </div>
      </div>

      <div role="tabpanel" aria-label={shownTab.title} className="space-y-5 p-4">
        {shownTab.groups.length === 0 ? (
          <p className="text-sm text-gray-500">
            {endpoint.fields.length === 0 ? "This call takes no inputs. Press Send." : `No ${shownTab.title.toLowerCase()} for this call.`}
          </p>
        ) : (
          shownTab.groups.map(({ group, fields }) => (
            <InputGroup
              key={group.id}
              group={group}
              fields={fields}
              values={values}
              fieldErrors={fieldErrors}
              devMode={devMode}
              onChange={setValue}
            />
          ))
        )}
        {devMode && <CodeBlock>{JSON.stringify(payload(), null, 2)}</CodeBlock>}
        <ErrorNote message={error} />
      </div>

      <section ref={responseRef} className="min-w-0 border-t border-gray-200" aria-live="polite" aria-label="Response">
        <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5">
          <h4 className="text-sm font-semibold text-gray-900">Response</h4>
          {request === "responded" && <StatusLine result={result} />}
        </div>
        <div className="p-4">
          {request !== "responded" ? (
            <div className="flex flex-col items-center gap-3 px-4 py-10 text-center text-sm text-gray-500">
              {busy && <OrbitLoader size={32} label="Sending request" />}
              {RESPONSE_PLACEHOLDER[request]}
            </div>
          ) : (
            <>
              {result.error_message && (
                <p className="mb-4 break-words text-sm text-rose-700 [overflow-wrap:anywhere]">{errorSummary(result.error_message)}</p>
              )}
              {!devMode && flat.length > 0 && (
                <dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-gray-50 p-3 sm:grid-cols-3">
                  {flat.slice(0, 9).map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <dt className="truncate text-xs text-gray-500">{k}</dt>
                      <dd className="truncate text-sm font-medium text-gray-900">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <ResponseViewer key={resultKey} body={body} devMode={devMode} name={endpoint.name} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/** "Tried 2 of 5 calls", with the step's way on beside it, above the workspace. */
function TryProgress({ tested, total, onBack, onNext }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <p className="flex items-center gap-1.5 text-sm text-gray-600">
        <CheckCircle2 className={`h-4 w-4 ${tested ? "text-emerald-600" : "text-gray-400"}`} />
        Tried {tested} of {total} {total === 1 ? "call" : "calls"}
        {tested === 0 && <span className="text-gray-400">· optional, you can skip this</span>}
      </p>
      <StepFooter onBack={onBack} onNext={onNext} nextLabel="Next: Go live" variant="secondary" className="gap-2" />
    </div>
  );
}

export function TryStep({ integration, tested, markTested, devMode, onBack, onNext }) {
  const endpoints = integration.endpoints;
  const [activeId, setActiveId] = useState(endpoints[0]?.id);
  const endpoint = endpoints.find((e) => e.id === activeId) || endpoints[0];

  return (
    <>
      <TryProgress tested={endpoints.filter((e) => tested.has(e.id)).length} total={endpoints.length} onBack={onBack} onNext={onNext} />
      {!endpoint ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-12 text-center text-sm text-gray-500">
          Add a call first, then try it here.
        </div>
      ) : (
        // One frame, as in Postman: the list and the request share a border and scroll on their own.
        <div className="grid overflow-hidden rounded-xl border border-gray-200 bg-white lg:h-[calc(100vh-14rem)] lg:min-h-[32rem] lg:grid-cols-[280px_minmax(0,1fr)]">
          <CallList endpoints={endpoints} activeId={endpoint.id} tested={tested} onSelect={setActiveId} />
          <TryCall key={endpoint.id} integration={integration} endpoint={endpoint} devMode={devMode} onTested={() => markTested(endpoint.id)} />
        </div>
      )}
    </>
  );
}
